import type {
  Contact,
  BankVerificationDetails
} from '@/lib/services/contacts/types';

/**
 * Backend verification status from ContactVerification entity
 * GET /matters/{matterId}/contact-verifications/{verificationId}
 */
export type BackendVerificationStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'VERIFIED'
  | 'REVIEW'
  | 'FAILED'
  | 'EXPIRED'
  | 'MANUAL'
  | 'SUPERSEDED';

/**
 * Backend confidence level from ContactVerification entity
 */
export type BackendConfidenceLevel =
  | 'HIGHEST'
  | 'HIGH'
  | 'REVIEW_REQUIRED'
  | 'FAILED';

/**
 * Bank verification data from the backend
 * Response from GET /matters/{matterId}/contact-verifications/{verificationId}
 */
export interface BankVerification {
  id: number;
  contactId: number;
  matterId: number;
  status: BackendVerificationStatus;
  confidenceLevel?: BackendConfidenceLevel | null;
  requestedAt: string;
  completedAt?: string | null;
  // Bank account details (from ConnectID or manual entry)
  connectIdAccountName?: string | null;
  connectIdBsb?: string | null;
  connectIdAccountNumber?: string | null;
  // Lawyer review
  lawyerApprovedAt?: string | null;
  lawyerApprovedByName?: string | null;
  lawyerNotes?: string | null;
}

/**
 * Verification attribution for display
 */
export interface VerificationAttribution {
  verifiedByName: string;
  verifiedAt: string; // ISO date string
}

/**
 * Display status for the UI - derived from backend status + confidence level
 */
export type VerificationDisplayStatus =
  | 'NOT_STARTED' // No verification record exists
  | 'REQUESTED' // Request sent, awaiting beneficiary
  | 'READY_FOR_REVIEW' // Details submitted, pending lawyer review
  | 'VERIFIED_HIGHEST' // Verified with highest confidence
  | 'VERIFIED_HIGH' // Verified with high confidence
  | 'VERIFIED_MANUAL'; // Manually verified by lawyer

/**
 * Single action type for the verification widget
 * Only one button is shown at a time
 */
export type VerificationAction =
  | 'request' // "Request Bank Details"
  | 'review' // "Review Verification"
  | 'view' // "View Verification"
  | 'edit' // "Edit Bank Details"
  | 'resend' // "Resend Request"
  | 'none'; // No action available

/**
 * Props for the verification actions widget
 */
export interface VerificationActionsWidgetProps {
  action: VerificationAction;
  onAction?: () => void;
}

/**
 * Props for the bank account section
 */
export interface BankAccountSectionProps {
  contact: Contact;
  verificationStatus: VerificationDisplayStatus;
  verifiedByName?: string | null;
  verifiedAt?: string | null;
  onAction?: () => void;
  onAddBankDetails?: () => void;
  onEditBankDetails?: () => void;
}

/**
 * Props for the simplified contact card
 */
export interface ContactCardSimplifiedProps {
  contact: Contact;
  verificationStatus?: VerificationDisplayStatus;
  verifiedByName?: string | null;
  verifiedAt?: string | null;
  onClick?: () => void;
  onIdUpload?: () => void;
  onBankAction?: () => void;
  onAddBankDetails?: () => void;
  onEditBankDetails?: () => void;
  className?: string;
}

/**
 * Minimal verification data needed for display status derivation
 */
export interface VerificationSummary {
  status: BackendVerificationStatus;
  confidenceLevel?: BackendConfidenceLevel | null;
}

/**
 * Get the active verification from an array of verifications.
 * Backend returns verifications sorted by requestedAt DESC, filtered to exclude SUPERSEDED/EXPIRED.
 * Returns the first (most recent) verification or null if array is empty.
 */
export function getActiveVerification<T extends VerificationSummary>(
  verifications: T[] | undefined | null
): T | null {
  if (!verifications || verifications.length === 0) {
    return null;
  }
  return verifications[0];
}

/**
 * Type guard to check if verification has lawyer approval fields
 */
function hasLawyerApproval(
  verification: VerificationSummary | BankVerification
): verification is BankVerification {
  return 'lawyerApprovedAt' in verification;
}

/**
 * Derive display status from backend verification data
 */
export function getVerificationDisplayStatus(
  verification: VerificationSummary | BankVerification | null | undefined
): VerificationDisplayStatus {
  if (!verification) {
    return 'NOT_STARTED';
  }

  // Check if lawyer has approved a REVIEW status verification
  if (
    verification.status === 'REVIEW' &&
    hasLawyerApproval(verification) &&
    verification.lawyerApprovedAt
  ) {
    // Lawyer approved - map to verified status based on confidence level
    if (verification.confidenceLevel === 'HIGHEST') {
      return 'VERIFIED_HIGHEST';
    }
    return 'VERIFIED_HIGH';
  }

  switch (verification.status) {
    case 'REQUESTED':
      return 'REQUESTED';
    case 'REVIEW':
      return 'READY_FOR_REVIEW';
    case 'VERIFIED':
      if (hasLawyerApproval(verification) && verification.lawyerApprovedAt) {
        if (verification.confidenceLevel === 'HIGHEST') {
          return 'VERIFIED_HIGHEST';
        }
        return 'VERIFIED_HIGH';
      }
      return 'READY_FOR_REVIEW';
    case 'MANUAL':
      return 'VERIFIED_MANUAL';
    case 'FAILED':
      return 'READY_FOR_REVIEW';
    case 'IN_PROGRESS':
      return 'REQUESTED';
    case 'EXPIRED':
    case 'SUPERSEDED':
    default:
      return 'NOT_STARTED';
  }
}

/**
 * Derive display status for a contact in a matter-scoped context.
 *
 * Verification is **per-matter** (LF-3696). The only signals consulted here
 * are the matter-scoped `verifications[]` array (Triple Lock activity on
 * this matter) and `contact.isBankVerified` (the ContactMatter flag —
 * `true` when the lawyer has either approved a verification or manually
 * confirmed the bank details on this matter).
 *
 * `contact.bankAccount.isVerified` is intentionally NOT read here: it is
 * the contact-global "standing belief" about the account itself, and
 * reading it in a matter-scoped surface causes verification to leak across
 * matters that happen to share the same contact.
 */
export function getContactVerificationStatus(
  contact: Contact
): VerificationDisplayStatus {
  const verification = getActiveVerification(contact.verifications);

  if (verification) {
    const displayStatus = getVerificationDisplayStatus(verification);
    // Completed Triple Lock statuses take priority over manual.
    if (
      displayStatus === 'VERIFIED_HIGHEST' ||
      displayStatus === 'VERIFIED_HIGH'
    ) {
      return displayStatus;
    }
    // Manual verification overrides pending states (REQUESTED, READY_FOR_REVIEW)
    // when the matter-scoped flag is set.
    if (contact.isBankVerified) {
      return 'VERIFIED_MANUAL';
    }
    if (displayStatus !== 'NOT_STARTED') {
      return displayStatus;
    }
  }

  if (contact.isBankVerified) {
    return 'VERIFIED_MANUAL';
  }

  return 'NOT_STARTED';
}

/**
 * Helper function to determine the single action based on verification status
 * Only one button is shown at a time
 */
export function getVerificationAction(
  contact: Contact,
  verificationStatus: VerificationDisplayStatus
): VerificationAction {
  if (!contact.isBeneficiary) return 'none';

  switch (verificationStatus) {
    case 'NOT_STARTED':
      return 'request'; // "Request Bank Details"
    case 'REQUESTED':
      return 'resend'; // "Resend Request"
    case 'READY_FOR_REVIEW':
      return 'review'; // "Review Verification"
    case 'VERIFIED_HIGHEST':
    case 'VERIFIED_HIGH':
      return 'view'; // "View Verification"
    case 'VERIFIED_MANUAL':
      return 'edit'; // "Edit Bank Details"
    default:
      return 'none';
  }
}

/**
 * Derive summary verification check values for table row display
 */
export function deriveVerificationChecks(contact: Contact): {
  identityVerified: boolean | null;
  ownershipVerified: boolean | null;
  nameConsistencyScore: number | null;
} {
  const verification = getActiveVerification(contact.verifications);
  if (!verification) {
    return {
      identityVerified: null,
      ownershipVerified: null,
      nameConsistencyScore: null
    };
  }

  if (verification.status === 'MANUAL') {
    return {
      identityVerified: false,
      ownershipVerified: false,
      nameConsistencyScore: null
    };
  }

  if (
    verification.status === 'REQUESTED' ||
    verification.status === 'IN_PROGRESS'
  ) {
    return {
      identityVerified: null,
      ownershipVerified: null,
      nameConsistencyScore: null
    };
  }

  // VERIFIED or REVIEW status — derive from confidenceLevel
  const cl = verification.confidenceLevel;
  if (cl === 'HIGHEST') {
    return {
      identityVerified: true,
      ownershipVerified: true,
      nameConsistencyScore: 100
    };
  }
  if (cl === 'HIGH') {
    return {
      identityVerified: true,
      ownershipVerified: true,
      nameConsistencyScore: 85
    };
  }
  if (cl === 'REVIEW_REQUIRED') {
    return {
      identityVerified: true,
      ownershipVerified: false,
      nameConsistencyScore: 50
    };
  }

  return {
    identityVerified: null,
    ownershipVerified: null,
    nameConsistencyScore: null
  };
}

/**
 * Derive review reasons from BankVerificationDetails
 */
export function deriveReviewReasons(
  details: BankVerificationDetails
): string[] {
  const reasons: string[] = [];
  if (!details.connectIdVerification) {
    reasons.push('ConnectID verification not completed');
  }
  if (details.zeptoValidation?.result === 'CLOSE_MATCH') {
    reasons.push('Account name is a close match');
  }
  if (
    details.zeptoValidation?.result === 'NO_MATCH' ||
    details.zeptoValidation?.result === 'INVALID'
  ) {
    reasons.push('Account name does not match');
  }
  if (details.zeptoValidation?.result === 'ACCOUNT_CLOSED') {
    reasons.push('Bank account is closed');
  }
  if (details.zeptoValidation?.result === 'UNAVAILABLE') {
    reasons.push('Bank verification was unavailable');
  }
  if (details.nameMatching?.componentMismatchReason) {
    reasons.push(details.nameMatching.componentMismatchReason);
  } else if (details.nameMatching && !details.nameMatching.matched) {
    reasons.push('Name on record differed from verified name');
  }
  return reasons;
}
